import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { INoboEvent } from "src/store/noboEvent/types";
import { NoboEventsView } from "./NoboEvents";
import { NOBO_EVENTS_VIEW } from "./NoboEvents";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const PENDING_APPROVAL_COLUMNS = [
  "jobNumber",
  "issuer",
  "recordDate",
  "solicitor",
  "created",
];

export const PENDING_DELIVERY_COLUMNS = [
  "jobNumber",
  "issuer",
  "recordDate",
  "solicitor",
  "scheduled",
  "created",
];

export const RECENTLY_DELIVERED_COLUMNS = [
  "jobNumber",
  "issuer",
  "recordDate",
  "solicitor",
  "scheduled",
  "delivered",
  "created",
];

export const HISTORICAL_COLUMNS = [
  "jobNumber",
  "issuer",
  "recordDate",
  "solicitor",
  "scheduled",
  "delivered",
  "created",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type UpcomingColumnData = {
  jobNumber: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  created: ColumnType;
};

export type ActiveColumnData = {
  jobNumber: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  scheduled: ColumnType;
  created: ColumnType;
};

export type RecentlyCompletedColumnData = {
  jobNumber: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  scheduled: ColumnType;
  delivered: ColumnType;
  created: ColumnType;
};

export type HistoricalColumnData = {
  jobNumber: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  scheduled: ColumnType;
  delivered: ColumnType;
  created: ColumnType;
};

export function getColumns(view: NoboEventsView) {
  if (view === NOBO_EVENTS_VIEW.PENDING_APPROVAL) {
    return PENDING_APPROVAL_COLUMNS;
  } else if (view === NOBO_EVENTS_VIEW.PENDING_DELIVERY) {
    return PENDING_DELIVERY_COLUMNS;
  } else if (view === NOBO_EVENTS_VIEW.RECENTLY_DELIVERED) {
    return RECENTLY_DELIVERED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData({
  getSort,
  onOrderingChange,
}: ColumnDataHelpers):
  | UpcomingColumnData
  | ActiveColumnData
  | RecentlyCompletedColumnData
  | HistoricalColumnData {
  return {
    jobNumber: {
      sort: getSort("jobNumber"),
      sortFn: (data: unknown) => onOrderingChange(data, "jobNumber"),
      style: "text",
      title: "Job Number",
    },
    issuer: {
      sort: getSort("issuer__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
      style: "text",
      title: "Issuer",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    recordDate: {
      sort: getSort("recordDate"),
      sortFn: (data: unknown) => onOrderingChange(data, "recordDate"),
      style: "text",
      title: "Record Date",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    created: {
      sort: getSort("created"),
      sortFn: (data: unknown) => onOrderingChange(data, "created"),
      style: "text",
      title: "Created",
    },
    scheduled: {
      sort: getSort("scheduled"),
      sortFn: (data: unknown) => onOrderingChange(data, "scheduled"),
      style: "text",
      title: "Scheduled",
    },
    delivered: {
      sort: getSort("delivered"),
      sortFn: (data: unknown) => onOrderingChange(data, "delivered"),
      style: "text",
      title: "Delivered",
    },
  };
}

export function getRowData(
  events: INoboEvent[],
  onRowClick: (event: INoboEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: INoboEvent;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const { id, issuer, recordDate, solicitor, created } = event;

    return {
      id,
      data: { event, idx },
      rowData: {
        jobNumber: {
          content: event.jobNumber || "-",
          clickHandler: handleRowClick,
        },
        issuer: {
          content: issuer.name,
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: dayjs(recordDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor.name,
          clickHandler: handleRowClick,
        },
        created: {
          content: dayjs(created).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        scheduled: {
          content: "-",
          clickHandler: handleRowClick,
        },
        delivered: {
          content: "-",
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
